import type { StepDraft } from './flag-drafts';
import type { FlagManager, PlanBody } from './useFlag';
import { useState } from 'react';
import { MAX_FLAG_STEPS } from 'definitions';
import { toast } from '@/components/ui/sonner';
import {
  MAX_HOLDBACK_PERCENT,
  marginToText,
  nextStepDraft,
  parseMarginPercent,
  parseStepDrafts,
  percentToBp,
  sameSteps,
  stepToDraft,
  usesGuardrail,
} from './flag-drafts';
import { guardrailMarginBp, isGuardrailPause } from './flag-guardrail';
import { planStartPreview } from './flag-status';

type PlanEditorCopy = {
  saved: string;
  started: string;
  lockedActive: string;
  holdbackInvalid: string;
  marginInvalid: string;
  guardrailNeedsVisitorId: string;
};

/**
 * The rollout plan editor: the steps as typed, the holdback while the flag is a draft, and the
 * plan's start, pause and resume. Unsaved steps stay on the page until Save; starting a plan
 * with unsaved edits is refused, so what runs is what is shown. Start and Resume are confirmed
 * with the step and share they pick up at.
 *
 * A step moves on by time, by the guardrail, or both (PER-340). The guardrail compares the
 * visitors each side enrolled during the step, which only the website visitor ID records, so
 * without it the guardrail gates cannot be picked and the API refuses them (409). The margin is
 * shown and saved once any step uses the guardrail; it can change while the plan runs, since
 * the API takes it without pausing (a new margin drops the last check).
 */
export const usePlanEditor = (manager: FlagManager, copy: PlanEditorCopy) => {
  const rollout = manager.rollout;
  const savedSteps = rollout?.steps ?? [];
  const savedHoldbackBp = rollout?.holdbackBp ?? 0;
  const planStatus = rollout?.planStatus ?? 'none';
  const [drafts, setDrafts] = useState<StepDraft[] | null>(null);
  const [holdbackText, setHoldbackText] = useState<string | null>(null);
  const [marginText, setMarginText] = useState<string | null>(null);
  const [confirmingStart, setConfirmingStart] = useState(false);
  const guardrailAvailable = !!manager.flag?.website?.visitorIdEnabled;

  const steps = drafts ?? savedSteps.map(stepToDraft);
  const parsed = parseStepDrafts(steps);
  const stepsDirty = drafts !== null && (!parsed.ok || !sameSteps(parsed.value, savedSteps));

  const holdback = holdbackText ?? String(savedHoldbackBp / 100);
  const holdbackBp = percentToBp(holdback);
  const holdbackValid = holdbackBp !== null && holdbackBp <= MAX_HOLDBACK_PERCENT * 100;
  const holdbackDirty = manager.isDraft && holdbackText !== null && holdbackBp !== savedHoldbackBp;

  const usesGuardrailGate = steps.some((step) => usesGuardrail(step.gate));
  const savedMarginBp = rollout ? guardrailMarginBp(rollout) : 0;
  const margin = marginText ?? marginToText(savedMarginBp);
  const marginBp = parseMarginPercent(margin);
  const marginEditable = usesGuardrailGate && !manager.isArchived && manager.canEdit;
  const marginDirty = marginEditable && marginText !== null && marginBp !== savedMarginBp;

  const isActive = planStatus === 'active';
  const isLocked = isActive || manager.isArchived || !manager.canEdit;
  const isDirty = stepsDirty || holdbackDirty || marginDirty;

  const blockingReason = !parsed.ok
    ? parsed.reason
    : stepsDirty && usesGuardrailGate && !guardrailAvailable
      ? copy.guardrailNeedsVisitorId
      : holdbackDirty && !holdbackValid
        ? copy.holdbackInvalid
        : marginDirty && marginBp === null
          ? copy.marginInvalid
          : undefined;

  const edit = (next: StepDraft[]) => setDrafts(next);
  const updateStep = (index: number, patch: Partial<StepDraft>) =>
    edit(steps.map((step, idx) => (idx === index ? { ...step, ...patch } : step)));
  const addStep = () => steps.length < MAX_FLAG_STEPS && edit([...steps, nextStepDraft(steps)]);
  const removeStep = (index: number) => edit(steps.filter((_, idx) => idx !== index));

  const discard = () => {
    setDrafts(null);
    setHoldbackText(null);
    setMarginText(null);
  };

  const save = async () => {
    if (!isDirty || blockingReason || !parsed.ok) return;
    const body: PlanBody = {
      ...(stepsDirty ? { steps: parsed.value } : {}),
      ...(holdbackDirty && holdbackBp !== null ? { holdbackBp } : {}),
      ...(marginDirty && marginBp !== null ? { guardrailMarginBp: marginBp } : {}),
    };
    if (await manager.savePlan(body)) {
      discard();
      toast.success(copy.saved);
    }
  };

  // Start (or resume) needs a live flag, saved steps, and nothing unsaved on the page.
  const canStart =
    manager.isLive && manager.canChange && !isActive && savedSteps.length > 0 && !isDirty && manager.pending === null;

  // Start and Resume can move the rollout at once (to the first step at or above the current
  // share), so they go through a confirm that says where the plan picks up.
  const startPreview = planStartPreview({ steps: savedSteps, rolloutBp: rollout?.rolloutBp ?? 0 });

  const start = async () => {
    if (!canStart) return;
    if (await manager.resumePlan()) {
      setConfirmingStart(false);
      toast.success(copy.started);
    }
  };

  return {
    steps,
    hasPlan: savedSteps.length > 0,
    planStatus,
    pauseReason: rollout?.pauseReason ?? null,
    pausedByGuardrail: !!rollout && isGuardrailPause(rollout),
    currentStep: rollout?.currentStep ?? 0,
    isActive,
    isLocked,
    lockedReason: isActive ? copy.lockedActive : undefined,
    canAddStep: !isLocked && steps.length < MAX_FLAG_STEPS,
    updateStep,
    addStep,
    removeStep,
    holdback,
    setHoldback: (value: string) => setHoldbackText(value),
    holdbackEditable: manager.isDraft && manager.canEdit,
    savedHoldbackBp,
    guardrailAvailable,
    // Why the guardrail choices are greyed out, where someone could otherwise pick one.
    showsVisitorIdNote: !guardrailAvailable && !isLocked && steps.length > 0,
    usesGuardrailGate,
    margin,
    // The margin the help line describes: what is typed while it parses, else what is saved.
    marginHelpBp: marginBp ?? savedMarginBp,
    setMargin: (value: string) => setMarginText(value),
    marginEditable,
    marginInvalid: marginText !== null && marginBp === null,
    isDirty,
    blockingReason,
    isSaving: manager.pending === 'plan',
    save,
    discard,
    canStart,
    canPause: isActive && manager.canChange && manager.pending === null,
    startPreview,
    confirmingStart,
    askStart: () => setConfirmingStart(true),
    cancelStart: () => setConfirmingStart(false),
    start,
    pause: manager.pausePlan,
  };
};

export type PlanEditorManager = ReturnType<typeof usePlanEditor>;
